import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { AnimatePresence, motion, useAnimationControls } from "framer-motion";
import { toast } from "sonner";
import { BubbleField } from "@/components/BubbleField";
import { Wave } from "@/components/Wave";
import { EmptyStomach } from "@/components/EmptyStomach";
import { QuickAddTray } from "@/components/QuickAddTray";
import { AiAddSheet } from "@/components/AiAddSheet";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { hapticHeavy, hapticLight } from "@/lib/native";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { MealLogList } from "@/components/MealLogList";
import {
  displayName,
  MACRO_COLORS,
  MACRO_KCAL,
  MACRO_LABELS,
  type BubbleEntry,
  type Macro,
  type MealSlot,
} from "@/lib/foods";
import { cloudRepository } from "@/lib/repository/cloud";
import { CloudAuthError, type FoodLogRow } from "@/lib/repository/types";
import { todayKST } from "@/lib/time";
import { ShareStoryButton } from "@/features/share-story/ShareStoryButton";
import { logsToBubbles, sumMacroTotals } from "@/lib/bubbleMapping";
import { getStoredBubbleMode, setStoredBubbleMode } from "@/lib/bubbleMode";
import type { BubbleMode } from "@/lib/foods";

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
const homeSearchSchema = z.object({
  // 유효한 YYYY-MM-DD만 허용. 없거나 형식이 깨진 값이면 오늘(파싱 시점 계산)로 폴백.
  // → 콜드 스타트·신규 설치·비정상 date 파라미터에서 항상 오늘 날짜가 보이도록.
  //   (default·catch를 함수로 둬 모듈 로드 시점에 고정되지 않고 매 파싱마다 계산)
  date: z
    .string()
    .regex(YMD_RE)
    .catch(() => todayKST())
    .default(() => todayKST()),
});

export const Route = createFileRoute("/")({
  validateSearch: zodValidator(homeSearchSchema),
  component: Index,
});

/** Parse YYYY-MM-DD as a local-date (no timezone shift). */
function parseYmd(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
  return new Date(y, (m || 1) - 1, d || 1);
}

/** YYYY-MM-DD from a Date in local time (used after Calendar select). */
function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const WEEKDAY_KO = ["일", "월", "화", "수", "목", "금", "토"];
function formatPastDate(ymd: string): string {
  const d = parseYmd(ymd);
  return `${d.getMonth() + 1}/${d.getDate()} ${WEEKDAY_KO[d.getDay()]}`;
}

function addDays(ymd: string, delta: number): string {
  const d = parseYmd(ymd);
  d.setDate(d.getDate() + delta);
  return toYmd(d);
}

function Index() {
  // Cloud food logs for the selected date (defaults to today via search param)
  const [logs, setLogs] = useState<FoodLogRow[]>([]);
  const [goalKcal, setGoalKcal] = useState(2000);
  const [aiSheetOpen, setAiSheetOpen] = useState(false);
  /** bowl 좌우 스와이프 추적 (날짜 이동) */
  const swipeStartRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const [hydrated, setHydrated] = useState(false);
  /** 즐겨찾기 food_id Set — MealLogList ActionSheet의 "즐겨찾기 추가/해제" 분기 */
  const [favFoodIds, setFavFoodIds] = useState<Set<string>>(new Set());

  // SSR 하이드레이션 불일치 방지: 서버·첫 클라 렌더는 기본 "kcal",
  // mount 후 localStorage에서 실제 저장값 로드.
  const [bubbleMode, setBubbleMode] = useState<BubbleMode>("kcal");
  useEffect(() => {
    setBubbleMode(getStoredBubbleMode());
  }, []);

  function changeBubbleMode(m: BubbleMode) {
    setBubbleMode(m);
    setStoredBubbleMode(m);
  }

  /** 즐겨찾기 토글 — optimistic + cloud sync. 실패 시 revert. */
  async function toggleFavorite(foodId: string) {
    const isFav = favFoodIds.has(foodId);
    const prev = favFoodIds;
    const next = new Set(favFoodIds);
    if (isFav) next.delete(foodId);
    else next.add(foodId);
    setFavFoodIds(next);
    try {
      if (isFav) await cloudRepository.favorites.remove(foodId);
      else await cloudRepository.favorites.add(foodId);
    } catch {
      setFavFoodIds(prev);
    }
  }

  const navigate = useNavigate();
  const { date: selectedDate } = Route.useSearch();
  const today = todayKST();
  const isToday = selectedDate === today;
  
  const [calendarOpen, setCalendarOpen] = useState(false);

  function setSelectedDate(next: string) {
    void navigate({ to: "/", search: { date: next } });
  }

  useEffect(() => {
    setHydrated(false);
    void loadLogsForDate(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // 설정에서 목표 변경 후 홈 복귀 시 항상 최신 goal 반영
  useEffect(() => {
    // 즐겨찾기 로드 — 모든 page mount에서 한 번
    cloudRepository.favorites
      .list()
      .then((favs) => setFavFoodIds(new Set(favs.map((f) => f.food_id))))
      .catch(() => {
        /* 401 등은 cloud.ts가 처리, 그 외 실패는 빈 Set으로 fallback */
      });

    cloudRepository.userGoal.get(todayKST()).then((goal) => {
      if (goal?.daily_kcal.value) setGoalKcal(goal.daily_kcal.value);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadLogsForDate(date: string) {
    try {
      const [fetched, goal] = await Promise.all([
        cloudRepository.foodLogs.listByDate(date),
        cloudRepository.userGoal.get(date),
      ]);
      setLogs(fetched);
      if (goal?.daily_kcal.value) setGoalKcal(goal.daily_kcal.value);
    } catch (e) {
      if (e instanceof CloudAuthError) {
        /* 401 → cloud.ts가 /auth/login으로 자동 redirect */
      } else {
        toast.error(`기록 로드 실패: ${e instanceof Error ? e.message : String(e)}`);
      }
    } finally {
      setHydrated(true);
    }
  }

  // Convert cloud logs to BubbleEntry[] for all existing UI components
  const entries = useMemo(() => logsToBubbles(logs, bubbleMode), [logs, bubbleMode]);

  // 요약·진척바·stage는 모드 무관 — 원본 logs에서 직접 합산.
  const totals = useMemo(() => sumMacroTotals(logs), [logs]);

  const totalKcal = Math.round(
    totals.carbs * MACRO_KCAL.carbs +
      totals.protein * MACRO_KCAL.protein +
      totals.fat * MACRO_KCAL.fat,
  );
  const rawPct = (totalKcal / goalKcal) * 100;
  const pct = Math.min(100, rawPct);

  const stage = rawPct >= 120 ? 4 : rawPct >= 100 ? 3 : rawPct >= 50 ? 2 : 1;
  const compression = stage === 4 ? 0.7 : stage === 3 ? 0.85 : 1;

  const bowlControls = useAnimationControls();
  const prevLenRef = useRef(0);
  useEffect(() => {
    const added = entries.length > prevLenRef.current;
    if (added && stage === 4) {
      bowlControls.start({
        x: [0, -4, 4, -4, 4, 0],
        transition: { duration: 0.3 },
      });
    }
    // 목표 초과 후 음식 추가될 때마다 햅틱 — stage 3(100~120%)=짧게, stage 4(≥120%)=강하게.
    // 네이티브(Capacitor)는 @capacitor/haptics, 웹은 navigator.vibrate fallback (lib/native.ts)
    if (added && stage >= 3) {
      void (stage === 4 ? hapticHeavy() : hapticLight());
    }
    prevLenRef.current = entries.length;
  }, [entries.length, stage, bowlControls]);

  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Maps logId → pending cloud DELETE so we can cancel on undo
  const pendingDeleteRef = useRef<Map<string, { log: FoodLogRow; timerId: ReturnType<typeof setTimeout> }>>(new Map());
  const bowlRef = useRef<HTMLDivElement>(null);
  // FAB 영역과 겹친 트레이 칩의 오탭 방지용 — QuickAddTray에 전달.
  const fabRef = useRef<HTMLButtonElement>(null);
  const [openResetDialog, setOpenResetDialog] = useState(false);

  // Flush all pending deletes on unmount
  useEffect(() => {
    return () => {
      for (const [logId, { timerId }] of pendingDeleteRef.current) {
        clearTimeout(timerId);
        // Fire-and-forget cloud DELETE on unmount
        void cloudRepository.foodLogs.remove(logId);
      }
      pendingDeleteRef.current.clear();
    };
  }, []);

  function changeSlot(foodLogId: string, slot: MealSlot) {
    setLogs((prev) =>
      prev.map((log) =>
        log.id === foodLogId ? { ...log, meal_slot: slot } : log,
      ),
    );
    cloudRepository.foodLogs.patch(foodLogId, { meal_slot: slot }).catch(() => {
      toast.error("슬롯 변경 저장 실패");
      void loadLogsForDate(selectedDate);
    });
  }

  const lastToastIdRef = useRef<string | number | null>(null);

  function removeByLogId(logId: string, label?: string) {
    // Find in current logs OR in pending-delete map (edge case: double-delete)
    const target = logs.find((l) => l.id === logId) ?? pendingDeleteRef.current.get(logId)?.log;
    if (!target) return;

    // Cancel any existing pending delete for this id (restart timer)
    const existing = pendingDeleteRef.current.get(logId);
    if (existing) {
      clearTimeout(existing.timerId);
      pendingDeleteRef.current.delete(logId);
    }

    // Dismiss previous undo toast
    if (lastToastIdRef.current != null) toast.dismiss(lastToastIdRef.current);

    // Optimistic remove from UI
    setLogs((prev) => prev.filter((l) => l.id !== logId));

    const tid = `undo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    lastToastIdRef.current = tid;
    const name = label ?? target.food?.name ?? "음식";

    // Schedule deferred cloud DELETE after 3 s
    const timerId = setTimeout(() => {
      pendingDeleteRef.current.delete(logId);
      lastToastIdRef.current = null;
      void cloudRepository.foodLogs.remove(logId).catch((e) => {
        // Revert on failure — add back to logs
        setLogs((prev) => [...prev, target]);
        if (e instanceof CloudAuthError) {
          /* 401 → cloud.ts가 /auth/login으로 자동 redirect */
        } else {
          toast.error("삭제 실패");
        }
      });
    }, 3000);

    pendingDeleteRef.current.set(logId, { log: target, timerId });

    toast(`${displayName(name)}을 지웠어요`, {
      id: tid,
      // 되돌리기 토스트는 하단(엄지 닿기 좋은 위치) + 3초로 명시
      position: "bottom-center",
      duration: 3000,
      action: {
        label: "되살리기",
        onClick: () => {
          const pending = pendingDeleteRef.current.get(logId);
          if (pending) {
            clearTimeout(pending.timerId);
            pendingDeleteRef.current.delete(logId);
          }
          // Restore to UI at original position (append — will re-sort by addedAt)
          setLogs((prev) => {
            if (prev.some((l) => l.id === logId)) return prev; // already there
            return [...prev, target];
          });
          lastToastIdRef.current = null;
        },
      },
    });

    // Keep undoTimerRef for compatibility (unused logic removed)
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      undoTimerRef.current = null;
    }, 3000);
  }

  function removeBubble(id: string) {
    // id format: "{logId}-{macroIndex}"
    const logId = id.split("-").slice(0, -1).join("-");
    void removeByLogId(logId);
  }

  function replaceQty(logId: string, newEntries: BubbleEntry[]) {
    // Compute aggregated macros from newEntries
    const carb = newEntries.filter((e) => e.macro === "carbs").reduce((s, e) => s + e.grams, 0);
    const protein = newEntries.filter((e) => e.macro === "protein").reduce((s, e) => s + e.grams, 0);
    const fat = newEntries.filter((e) => e.macro === "fat").reduce((s, e) => s + e.grams, 0);
    // 편집 시트가 전달한 공식 kcal·실제 grams 우선. 없으면 매크로 기반 폴백.
    const first = newEntries[0];
    const kcal = first?.logKcal ?? Math.round(carb * 4 + protein * 4 + fat * 9);
    const grams = first?.logGrams ?? Math.round(carb + protein + fat);

    setLogs((prev) =>
      prev.map((log) =>
        log.id === logId
          ? { ...log, carb_g: carb, protein_g: protein, fat_g: fat, kcal, grams }
          : log,
      ),
    );
  }

  function reset() {
    setOpenResetDialog(true);
  }

  async function confirmReset() {
    const toDelete = [...logs];
    setLogs([]);
    setOpenResetDialog(false);

    try {
      await Promise.all(toDelete.map((l) => cloudRepository.foodLogs.remove(l.id)));
      toast(isToday ? "오늘 기록을 지웠어요" : `${formatPastDate(selectedDate)} 기록을 지웠어요`);
    } catch (e) {
      // Revert
      setLogs(toDelete);
      if (e instanceof CloudAuthError) {
        /* 401 → cloud.ts가 /auth/login으로 자동 redirect */
      } else {
        toast.error("비우기 실패");
      }
    }
  }

  const fieldWidth = 375;
  const fieldHeight = 380;

  return (
    <div className="min-h-screen w-full bg-white flex justify-center">
      <main className="w-full max-w-[375px] flex flex-col">
        {/* 타이틀 행 — main의 직접 자식이어야 전체 스크롤 구간에서 sticky 고정 동작 */}
        <div className="sticky top-[env(safe-area-inset-top)] z-20 bg-white px-5 py-2">
          <div className="flex items-center justify-between gap-2 py-1">
            <div className="flex items-center gap-1 min-w-0">
              <button
                type="button"
                onClick={() => setSelectedDate(addDays(selectedDate, -1))}
                aria-label="이전 날짜"
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="rounded-md px-1 text-lg font-semibold text-neutral-900 hover:bg-neutral-100 tabular-nums"
                  >
                    {isToday ? "오늘의 칼로리" : formatPastDate(selectedDate)}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={parseYmd(selectedDate)}
                    onSelect={(d) => {
                      if (d) {
                        setSelectedDate(toYmd(d));
                        setCalendarOpen(false);
                      }
                    }}
                    disabled={(d) => toYmd(d) > today}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              <button
                type="button"
                onClick={() => setSelectedDate(addDays(selectedDate, 1))}
                aria-label="다음 날짜"
                disabled={isToday}
                className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              {!isToday && (
                <button
                  onClick={() => setSelectedDate(today)}
                  className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-200"
                >
                  오늘
                </button>
              )}
              <button
                onClick={reset}
                className="text-xs text-neutral-400 hover:text-neutral-600"
              >
                비우기
              </button>
              <ShareStoryButton
                data={{
                  date: selectedDate,
                  totalKcal,
                  goalKcal: goalKcal,
                  carbG: totals.carbs,
                  proteinG: totals.protein,
                  fatG: totals.fat,
                  bubbles: entries,
                }}
              />
            </div>
          </div>
        </div>

        {/* 좌우 스와이프 영역 — kcal·macros·bowl */}
        <div
          className="touch-pan-y"
          onPointerDown={(e) => {
            swipeStartRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
          }}
          onPointerUp={(e) => {
            const s = swipeStartRef.current;
            swipeStartRef.current = null;
            if (!s) return;
            const dx = e.clientX - s.x;
            const dy = e.clientY - s.y;
            const dt = Date.now() - s.t;
            if (dt > 700) return;
            if (Math.abs(dx) < 60 || Math.abs(dy) > 40) return;
            if (dx > 0) setSelectedDate(addDays(selectedDate, -1));
            else setSelectedDate(addDays(selectedDate, 1));
          }}
          onPointerCancel={() => {
            swipeStartRef.current = null;
          }}
        >
        {/* 칼로리 · 진척바 · 탄단지 — 스크롤과 함께 */}
        <div className="px-5 pb-3">
          <div className="mt-1 flex items-baseline gap-1">
            <span className="text-3xl font-bold tabular-nums text-neutral-900">
              {totalKcal}
            </span>
            <span className="text-sm text-neutral-400">/ {goalKcal} kcal</span>
          </div>

          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
            <motion.div
              className="h-full rounded-full"
              style={{
                background: `linear-gradient(90deg, ${MACRO_COLORS.carbs}, ${MACRO_COLORS.protein}, ${MACRO_COLORS.fat})`,
              }}
              initial={false}
              animate={{ width: `${pct}%` }}
              transition={{ type: "spring", stiffness: 120, damping: 20 }}
            />
          </div>

          <div className="mt-3 flex justify-between text-[11px] text-neutral-500">
            {(["carbs", "protein", "fat"] as Macro[]).map((m) => (
              <div key={m} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: MACRO_COLORS[m] }}
                />
                <span>
                  {MACRO_LABELS[m]} {Math.round(totals[m])}g
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Bubble field — bowl/stomach container (스와이프는 상위 div에서 처리) */}
        <section className="relative mx-auto px-5" style={{ width: fieldWidth }}>
          <motion.div
            ref={bowlRef}
            data-share-bubble
            animate={bowlControls}
            className={`relative overflow-hidden shadow-inner ${
              stage === 3 ? "animate-pulse" : ""
            }`}
            style={{
              width: fieldWidth - 40,
              height: fieldHeight,
              borderRadius: "44% 44% 38% 38% / 18% 18% 50% 50%",
              background:
                "radial-gradient(120% 80% at 50% 10%, #f8fafc 0%, #eef2f6 60%, #e5eaf0 100%)",
              border: `${stage >= 2 ? 2 : 1}px solid ${
                stage >= 3
                  ? "rgba(255,107,107,0.85)"
                  : stage === 2
                    ? "rgba(255,193,7,0.85)"
                    : "rgba(229,231,235,0.7)"
              }`,
              boxShadow:
                stage >= 3
                  ? "0 0 24px rgba(255,107,107,0.45), inset 0 4px 12px rgba(0,0,0,0.04)"
                  : stage === 2
                    ? "0 0 22px rgba(255,193,7,0.45), inset 0 4px 12px rgba(0,0,0,0.04)"
                    : "inset 0 4px 12px rgba(0,0,0,0.04)",
              transition: "border-color 0.4s, box-shadow 0.4s",
            }}
          >
            <AnimatePresence>
              <BubbleField
                bubbles={entries}
                width={fieldWidth - 40}
                height={fieldHeight}
                onRemove={removeBubble}
                goalKcal={goalKcal}
                compression={compression}
              />
            </AnimatePresence>

            {/* Wave at bottom of bowl */}
            <Wave width={fieldWidth - 40} height={48} />

            {/* Empty state */}
            {entries.length === 0 && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <EmptyStomach />
              </div>
            )}
          </motion.div>

          {/* Stage warning — 평소엔 안내 문구, stage>=3엔 경고로 대체 */}
          <AnimatePresence mode="wait">
            <motion.p
              key={stage >= 3 ? `warn-${stage}` : "hint"}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className={`mt-2 text-center text-xs font-medium ${
                stage === 4
                  ? "text-red-500"
                  : stage === 3
                    ? "text-amber-600"
                    : "text-neutral-400"
              }`}
            >
              {stage === 4
                ? "배 터질 것 같아요 😵"
                : stage === 3
                  ? "목표를 초과했어요"
                  : "버블을 탭하면 제거됩니다"}
            </motion.p>
          </AnimatePresence>

          <div className="mt-3 flex justify-center">
            <div className="inline-flex rounded-full bg-neutral-100 p-0.5">
              {(["kcal", "macro"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  aria-pressed={bubbleMode === m}
                  onClick={() => changeBubbleMode(m)}
                  className={`px-3 py-1 text-[11px] font-semibold rounded-full transition-colors ${
                    bubbleMode === m
                      ? "bg-neutral-900 text-white"
                      : "text-neutral-500 active:text-neutral-800"
                  }`}
                >
                  {m === "kcal" ? "칼로리" : "탄단지"}
                </button>
              ))}
            </div>
          </div>
        </section>
        </div>
        {/* /스와이프 wrapper */}

        {/* Quick add tray */}
        <QuickAddTray
          bubbleContainerRef={bowlRef}
          fabRef={fabRef}
          loggedDate={selectedDate}
          onAdded={(log) => {
            setLogs((prev) => [...prev, log]);
          }}
        />

        {hydrated && (
          <MealLogList
            entries={entries}
            onChangeSlot={changeSlot}
            onDelete={(logId, foodName) => removeByLogId(logId, foodName)}
            onReplaceQty={replaceQty}
            favFoodIds={favFoodIds}
            onToggleFavorite={(foodId) => void toggleFavorite(foodId)}
          />
        )}

        <AlertDialog open={openResetDialog} onOpenChange={setOpenResetDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{isToday ? "오늘 기록을 모두 지울까요?" : `${formatPastDate(selectedDate)} 기록을 모두 지울까요?`}</AlertDialogTitle>
              <AlertDialogDescription className="text-[13px] text-neutral-500">
                지운 기록은 되돌릴 수 없어요.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="!flex-row !justify-between !space-x-2">
              <AlertDialogAction
                onClick={() => void confirmReset()}
                className="flex-1 bg-white text-red-600 border border-neutral-200 hover:bg-red-50"
              >
                지우기
              </AlertDialogAction>
              <AlertDialogCancel
                onClick={() => setOpenResetDialog(false)}
                className="flex-1 mt-0 bg-neutral-900 text-white hover:bg-neutral-800 border-0"
              >
                취소
              </AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>

      {/* FAB — AI 시트 트리거. Material Design FAB 표준 (56pt button + 24pt icon) */}
      <button
        ref={fabRef}
        onClick={() => setAiSheetOpen(true)}
        aria-label="AI로 음식 추가"
        className="fixed z-40 flex items-center justify-center rounded-full bg-neutral-900 text-white shadow-lg active:scale-95 transition hover:bg-neutral-800"
        style={{
          // 탭바와 동일한 base 오프셋(max(safe-area, ad+8)) + 탭바 높이만큼 위로(56px).
          bottom:
            "calc(max(env(safe-area-inset-bottom), var(--ad-banner-height, 0px) + 8px) + 56px)",
          right: 20,
          width: 56,
          height: 56,
        }}
      >
        <Sparkles className="w-6 h-6" />
      </button>

      <AiAddSheet
        open={aiSheetOpen}
        onOpenChange={setAiSheetOpen}
        loggedDate={selectedDate}
        onRegistered={() => {
          // logs 재조회 (Optimistic insert 대신 — AI 결과/사진 등 신뢰성 위해 서버 단일 진실)
          void (async () => {
            try {
              const fetched = await cloudRepository.foodLogs.listByDate(selectedDate);
              setLogs(fetched);
            } catch {
              /* 다음 탐색에서 자동 복구 */
            }
          })();
        }}
      />
    </div>
  );
}

